/**
 * product-statement-hotspot.js
 * - グローバルイベント登録なし（各 .pshs-root 内部のみ操作）
 * - jQuery / 外部ライブラリ不使用
 * - 複数セクション・テーマエディタでの再読込に対応
 * - body へのスクロールロックは行わない（背面ページは常にスクロール可能）
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 768px)';
  var EDGE_MARGIN = 12;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initFadeIn(root) {
    var items = root.querySelectorAll('.pshs-fade');
    if (!items.length) return;

    items.forEach(function (el) {
      var delay = el.getAttribute('data-pshs-delay') || '0';
      el.style.setProperty('--pshs-fade-delay', delay);
    });

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { root: null, threshold: 0.2 }
    );

    items.forEach(function (el) {
      observer.observe(el);
    });
  }

  /**
   * SPカードが画面外へはみ出さないよう、開いた直後に位置補正する。
   * - 下方向のスペースが足りない場合はホットスポットの上側に開く
   * - 左右のスペースが足りない場合は水平シフト量を CSS変数で補正する
   */
  function positionSpCard(hotspot) {
    var card = hotspot.querySelector('[data-pshs-card]');
    if (!card) return;

    hotspot.removeAttribute('data-resolved-direction-sp');
    card.style.removeProperty('--pshs-sp-shift');

    var vh = window.innerHeight;
    var rect = card.getBoundingClientRect();

    if (rect.bottom > vh - EDGE_MARGIN) {
      hotspot.setAttribute('data-resolved-direction-sp', 'above');
      rect = card.getBoundingClientRect(); // 属性変更後のレイアウトを再測定
    }

    var vw = window.innerWidth;
    var shift = 0;
    if (rect.left < EDGE_MARGIN) {
      shift = EDGE_MARGIN - rect.left;
    } else if (rect.right > vw - EDGE_MARGIN) {
      shift = (vw - EDGE_MARGIN) - rect.right;
    }
    if (shift !== 0) {
      card.style.setProperty('--pshs-sp-shift', shift + 'px');
    }
  }

  function resolvePcDirection(hotspot) {
    var pref = hotspot.getAttribute('data-direction') || 'auto';
    if (pref === 'left' || pref === 'right') {
      hotspot.setAttribute('data-resolved-direction', pref);
      return;
    }
    // auto: 画面中央より右にあるホットスポットは左展開、左にあるものは右展開
    var rect = hotspot.getBoundingClientRect();
    var vw = window.innerWidth;
    var resolved = rect.left > vw / 2 ? 'left' : 'right';
    hotspot.setAttribute('data-resolved-direction', resolved);
  }

  function keepPcCardInViewport(hotspot) {
    var card = hotspot.querySelector('[data-pshs-card]');
    if (!card) return;
    var rect = card.getBoundingClientRect();
    var vw = window.innerWidth;

    if (rect.right > vw - EDGE_MARGIN) {
      hotspot.setAttribute('data-resolved-direction', 'left');
    } else if (rect.left < EDGE_MARGIN) {
      hotspot.setAttribute('data-resolved-direction', 'right');
    }
  }

  function setBackdrop(root, show) {
    var backdrop = root.querySelector('[data-pshs-backdrop]');
    if (!backdrop) return;
    backdrop.hidden = !show;
    if (show) {
      root.classList.add('pshs-sp-open');
    } else {
      root.classList.remove('pshs-sp-open');
    }
  }

  function closeHotspot(hotspot) {
    if (!hotspot.classList.contains('is-open')) return;
    var trigger = hotspot.querySelector('[data-pshs-trigger]');
    var card = hotspot.querySelector('[data-pshs-card]');
    hotspot.classList.remove('is-open');
    hotspot.removeAttribute('data-resolved-direction-sp');
    if (card) {
      card.setAttribute('aria-hidden', 'true');
      card.style.removeProperty('--pshs-sp-shift');
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  function closeAll(root, focusTrigger) {
    var openOnes = root.querySelectorAll('.pshs-hotspot.is-open');
    var hadOpen = openOnes.length > 0;
    openOnes.forEach(function (h) {
      closeHotspot(h);
    });
    if (hadOpen) {
      setBackdrop(root, false);
      if (focusTrigger) {
        focusTrigger.focus({ preventScroll: true });
      }
    }
  }

  function openHotspot(root, hotspot) {
    closeAll(root, null);

    var trigger = hotspot.querySelector('[data-pshs-trigger]');
    var card = hotspot.querySelector('[data-pshs-card]');
    if (!trigger || !card) return;

    var mobile = isMobile();

    if (!mobile) {
      resolvePcDirection(hotspot);
    }

    hotspot.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    card.setAttribute('aria-hidden', 'false');

    if (mobile) {
      setBackdrop(root, true);
      window.requestAnimationFrame(function () {
        positionSpCard(hotspot);
      });
    } else {
      window.requestAnimationFrame(function () {
        keepPcCardInViewport(hotspot);
      });
    }

    var closeBtn = card.querySelector('[data-pshs-close]');
    if (closeBtn) {
      closeBtn.focus({ preventScroll: true });
    }
  }

  function initHotspots(root) {
    var hotspots = root.querySelectorAll('[data-pshs-hotspot]');

    hotspots.forEach(function (hotspot) {
      var trigger = hotspot.querySelector('[data-pshs-trigger]');
      var closeBtn = hotspot.querySelector('[data-pshs-close]');
      var card = hotspot.querySelector('[data-pshs-card]');

      if (trigger) {
        trigger.addEventListener('click', function (e) {
          e.stopPropagation();
          if (hotspot.classList.contains('is-open')) {
            closeAll(root, trigger);
          } else {
            openHotspot(root, hotspot);
          }
        });
      }

      if (closeBtn) {
        closeBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          closeAll(root, trigger);
        });
      }

      // カード内クリックが外側クリック判定へ伝播しないようにする
      if (card) {
        card.addEventListener('click', function (e) {
          e.stopPropagation();
        });
      }
    });

    // 外側クリック／タップで閉じる（ドラッグ由来のスクロール操作は誤爆させない）
    root.addEventListener('click', function (e) {
      if (!e.target.closest('[data-pshs-hotspot]')) {
        closeAll(root, null);
      }
    });

    var backdrop = root.querySelector('[data-pshs-backdrop]');
    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closeAll(root, null);
      });
    }

    // Escapeで閉じる
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        var opened = root.querySelector('.pshs-hotspot.is-open');
        if (opened) {
          var t = opened.querySelector('[data-pshs-trigger]');
          closeAll(root, t);
        }
      }
    });

    // ブレークポイント切替時は開いているカードをリセット（PC/SPでカード実装が異なるため）
    var mq = window.matchMedia(MOBILE_QUERY);
    var onBreakpointChange = function () {
      closeAll(root, null);
    };
    if (mq.addEventListener) {
      mq.addEventListener('change', onBreakpointChange);
    } else if (mq.addListener) {
      mq.addListener(onBreakpointChange);
    }

    // リサイズ時、開いているSPカードの位置を再計算
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        if (!isMobile()) return;
        var opened = root.querySelector('.pshs-hotspot.is-open');
        if (opened) positionSpCard(opened);
      }, 150);
    });
  }

  function initSection(root) {
    if (root.getAttribute('data-pshs-inited') === 'true') return;
    root.setAttribute('data-pshs-inited', 'true');
    initFadeIn(root);
    initHotspots(root);
  }

  function initAll(context) {
    var ctx = context || document;
    var roots = ctx.querySelectorAll('[data-pshs-root]');
    roots.forEach(function (root) {
      initSection(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Shopify テーマエディタでのセクション再読込対応（重複初期化は data-pshs-inited で防止）
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      initAll(e.target);
    });
  }
})();
